/*
 * Known OpenAI-compatible endpoints.
 *
 * The base URLs are the durable part and are worth hardcoding — they change
 * rarely. Model names are NOT: they are deprecated and replaced constantly, so
 * `models` here is a starting suggestion, shown as a datalist the user can
 * type over, and the form also offers "Fetch models" which asks the endpoint
 * itself via GET /models. A hardcoded model list is stale the month you ship
 * it; the live list never is.
 *
 * `selfHosted` marks endpoints on a private address. The server's SSRF guard
 * (backend/url_guard.py) rejects those by default — a user-supplied base_url
 * becomes the authority of a request made from inside our network. The form
 * says so up front rather than letting the save fail with a surprise.
 */
export const PROVIDER_PRESETS = [
  {
    id: "openai",
    label: "OpenAI",
    baseUrl: "https://api.openai.com/v1",
    keyHint: "sk-…",
    types: {
      llm: ["gpt-4o", "gpt-4o-mini", "o3-mini"],
      tts: ["gpt-4o-mini-tts", "tts-1-hd", "tts-1"],
      stt: ["gpt-4o-transcribe", "whisper-1"],
    },
  },
  {
    id: "groq",
    label: "Groq",
    baseUrl: "https://api.groq.com/openai/v1",
    keyHint: "gsk_…",
    types: {
      llm: ["llama-3.3-70b-versatile", "llama-3.1-8b-instant"],
      stt: ["whisper-large-v3", "whisper-large-v3-turbo"],
    },
  },
  {
    id: "gemini",
    label: "Google Gemini",
    baseUrl: "https://generativelanguage.googleapis.com/v1beta/openai/",
    keyHint: "AIza…",
    note: "Google's OpenAI-compatibility layer. Chat models only.",
    types: { llm: ["gemini-2.0-flash", "gemini-1.5-pro"] },
  },
  {
    id: "nvidia-nim",
    label: "NVIDIA NIM",
    baseUrl: "https://integrate.api.nvidia.com/v1",
    keyHint: "nvapi-…",
    types: { llm: ["meta/llama-3.3-70b-instruct", "nvidia/llama-3.1-nemotron-70b-instruct"] },
  },
  {
    id: "deepseek",
    label: "DeepSeek",
    baseUrl: "https://api.deepseek.com/v1",
    keyHint: "sk-…",
    types: { llm: ["deepseek-chat", "deepseek-reasoner"] },
  },
  {
    id: "mistral",
    label: "Mistral",
    baseUrl: "https://api.mistral.ai/v1",
    keyHint: "…",
    types: { llm: ["mistral-large-latest", "mistral-small-latest"] },
  },
  {
    id: "xai",
    label: "xAI (Grok)",
    baseUrl: "https://api.x.ai/v1",
    keyHint: "xai-…",
    types: { llm: ["grok-2-latest"] },
  },
  {
    id: "together",
    label: "Together AI",
    baseUrl: "https://api.together.xyz/v1",
    keyHint: "…",
    types: { llm: ["meta-llama/Llama-3.3-70B-Instruct-Turbo"] },
  },
  {
    id: "openrouter",
    label: "OpenRouter",
    baseUrl: "https://openrouter.ai/api/v1",
    keyHint: "sk-or-…",
    note: "Routes to many vendors behind one key.",
    types: { llm: ["openai/gpt-4o", "anthropic/claude-3.5-sonnet"] },
  },
  {
    id: "fireworks",
    label: "Fireworks AI",
    baseUrl: "https://api.fireworks.ai/inference/v1",
    keyHint: "fw_…",
    types: { llm: ["accounts/fireworks/models/llama-v3p3-70b-instruct"] },
  },
  {
    id: "cerebras",
    label: "Cerebras",
    baseUrl: "https://api.cerebras.ai/v1",
    keyHint: "csk-…",
    types: { llm: ["llama-3.3-70b"] },
  },
  {
    id: "ollama",
    label: "Ollama (self-hosted)",
    baseUrl: "http://localhost:11434/v1",
    keyHint: "any value — Ollama ignores it",
    selfHosted: true,
    types: { llm: ["llama3.2", "qwen2.5"] },
  },
  {
    id: "vllm",
    label: "vLLM (self-hosted)",
    baseUrl: "http://localhost:8000/v1",
    keyHint: "token you launched vLLM with",
    selfHosted: true,
    types: { llm: [] },
  },
  {
    id: "lmstudio",
    label: "LM Studio (self-hosted)",
    baseUrl: "http://localhost:1234/v1",
    keyHint: "any value",
    selfHosted: true,
    types: { llm: [] },
  },
  {
    id: "llamacpp",
    label: "llama.cpp server (self-hosted)",
    baseUrl: "http://localhost:8080/v1",
    keyHint: "any value",
    selfHosted: true,
    types: { llm: [] },
  },
];

/** Presets that can serve this pipeline step. */
export function presetsForType(type) {
  return PROVIDER_PRESETS.filter((p) => Array.isArray(p.types?.[type]));
}

export function findPreset(id) {
  return PROVIDER_PRESETS.find((p) => p.id === id) || null;
}
